import React, {ReactNode, useEffect, useRef} from 'react'
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Pagination, Mousewheel, Keyboard} from 'swiper/modules'
import type SwiperType from 'swiper'
import {useHeaderSize, useViewport} from '_customHooks'
// import {useHeaderDispatch} from '_context'

interface AnimatedSlideProps {
    data: {
        title_animation_type: 'no-animation' | 'fixed' | 'size' | 'attached' | 'fade' | 'carousel'
        module_attached?: boolean
        svg?: {url: string; metadata: {height: number; width: number}}
        title?: string
        cta_text?: string
        cta_url?: string
        cta2_text?: string
        cta2_url?: string
        cta_inline_d?: boolean
        cta_inline_m?: boolean
        content_color?: string
        content_align?: 'left' | 'center' | 'right'
        class_name?: string
        custom_css?: string
    }
    moduleItems: Array<ReactNode>
}

const AnimatedSlide: React.FC<AnimatedSlideProps> = ({data, moduleItems}) => {
    const {
        title_animation_type,
        module_attached,
        svg,
        title,
        cta_text,
        cta_url,
        cta2_text,
        cta2_url,
        cta_inline_d,
        cta_inline_m,
        content_color,
        content_align,
        class_name,
        custom_css,
    } = data
    const {isDesktop} = useViewport()
    const {headerSize, accessibilityBannerSize} = useHeaderSize(true)
    const {headerSize: headerSizeNotCollapsed} = useHeaderSize(false)

    const prevSlideIndex = useRef<number>(0)

    const styles = useMultiStyleConfig('AnimatedSlide', {headerSize, headerSizeNotCollapsed, accessibilityBannerSize}) as Record<string, any>

    const handleSlideChange = (swiper: SwiperType): void => {
        const lastSlideIndex = swiper.slides.length - 1

        // scroll on page top when scrolling up from last slide
        if (prevSlideIndex.current === lastSlideIndex) {
            window.scrollTo({top: 1, behavior: 'smooth'})
            // window.scrollTo({top: 0, behavior: 'smooth'})
        }

        // collapse menu when scrolling down from first slide
        if (prevSlideIndex.current === 0) {
            window.scrollTo({top: 1, behavior: 'instant'})
            // setExpanded(false)
        }

        // expand menu when scrolling up to first slide
        if (swiper.realIndex === 0) {
            window.scrollTo({top: 0, behavior: 'instant'})
            // setExpanded(true)
        }

        // handle scroll on mobile
        if (!isDesktop) {
            if (swiper.realIndex === lastSlideIndex) {
                document.body.style.overflow = 'visible'
            }

            if (prevSlideIndex.current === lastSlideIndex) {
                document.body.style.overflow = 'hidden'
            }
        }

        // update ref
        prevSlideIndex.current = swiper.realIndex
    }

    useEffect(() => {
        if (!isDesktop) {
            document.body.style.overflow = 'hidden'
        }
    }, [])

    const renderSwiper = () => (
        <Swiper
            direction={'vertical'}
            pagination={{
                enabled: isDesktop,
                clickable: true,
            }}
            mousewheel={{
                releaseOnEdges: isDesktop,
                enabled: isDesktop,
            }}
            keyboard
            speed={1000}
            modules={[Pagination, Mousewheel, Keyboard]}
            className="mySwiper"
            onRealIndexChange={(swiper) => {
                handleSlideChange(swiper)
            }}
        >
            {moduleItems?.map((component, idx) => (
                <SwiperSlide key={idx}>
                    <Box {...styles.moduleItemWrapper}>{component}</Box>
                </SwiperSlide>
            ))}
        </Swiper>
    )

    return (
        <>
            <Box data-testid="animated-slide" sx={styles.container}>
                {title_animation_type === 'carousel' && renderSwiper()}
            </Box>
        </>
    )
}

export {AnimatedSlide}
